import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useAnalytics } from '@/hooks/useAnalytics';

type IntentOption = 
  | 'parent_family'
  | 'partner'
  | 'friend'
  | 'someone_i_miss'
  | 'not_sure';

interface OnboardingFlowProps {
  onComplete: (variant: 'full' | 'condensed', selectedIntent?: IntentOption) => void;
  variant?: 'full' | 'condensed';
}

const intentOptions: { value: IntentOption; label: string }[] = [
  { value: 'parent_family', label: 'A parent or family member' },
  { value: 'partner', label: 'A partner' },
  { value: 'friend', label: 'A friend' },
  { value: 'someone_i_miss', label: 'Someone I miss' },
  { value: 'not_sure', label: "I'm not sure yet" },
];

export function OnboardingFlow({ onComplete, variant = 'full' }: OnboardingFlowProps) {
  const [currentScreen, setCurrentScreen] = useState(1);
  const [selectedIntent, setSelectedIntent] = useState<IntentOption | null>(null);
  const { trackEvent } = useAnalytics();
  const totalScreens = variant === 'full' ? 3 : 1;

  const handleNext = () => {
    if (variant === 'condensed' || currentScreen >= totalScreens) {
      trackEvent('onboarding_completed', { 
        variant, 
        intent: selectedIntent,
        screens_viewed: currentScreen 
      });
      onComplete(variant, selectedIntent || undefined);
    } else {
      setCurrentScreen((prev) => prev + 1);
    }
  };

  const handleSkipToTemplates = () => {
    trackEvent('onboarding_skipped_to_templates', { variant, screen: currentScreen });
    onComplete(variant, selectedIntent || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-lg bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
      >
        <AnimatePresence mode="wait">
          {currentScreen === 1 && (
            <Screen1Welcome 
              key="welcome" 
              onNext={handleNext} 
              isCondensed={variant === 'condensed'}
            />
          )}
          {currentScreen === 2 && variant === 'full' && (
            <Screen2Intent
              key="intent"
              selectedIntent={selectedIntent}
              onSelectIntent={setSelectedIntent}
              onNext={handleNext}
            />
          )}
          {currentScreen === 3 && variant === 'full' && (
            <Screen3Permission
              key="permission"
              onChooseStyle={handleNext}
              onWriteOwn={handleSkipToTemplates}
            />
          )}
        </AnimatePresence>

        {/* Progress dots */}
        {variant === 'full' && (
          <div className="flex justify-center gap-2 pb-6">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`w-2 h-2 rounded-full transition-colors ${
                  step === currentScreen ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

// Screen 1: Welcome
function Screen1Welcome({ 
  onNext, 
  isCondensed 
}: { 
  onNext: () => void; 
  isCondensed: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-8 md:p-10 text-center"
    >
      <div className="mb-6">
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="inline-flex p-4 rounded-full bg-primary/10"
        >
          <Heart className="h-10 w-10 text-primary" fill="currentColor" />
        </motion.div>
      </div>

      <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
        Say it now — while they can feel it.
      </h1>

      <p className="text-muted-foreground text-lg leading-relaxed mb-8">
        Most people don't hear how much they matter until it's too late.
        <br />
        <span className="text-foreground font-medium">
          Say It NOW
        </span>{' '}
        helps you share those words while they can still be received.
      </p>

      <Button onClick={onNext} size="lg" className="gap-2 px-8">
        Begin
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="text-sm text-muted-foreground mt-6">
        Nothing is shared without your permission.
      </p>
    </motion.div>
  );
}

// Screen 2: Intent Setting
function Screen2Intent({
  selectedIntent,
  onSelectIntent,
  onNext,
}: {
  selectedIntent: IntentOption | null;
  onSelectIntent: (intent: IntentOption) => void;
  onNext: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-8 md:p-10"
    >
      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2 text-center">
        Is there someone you've been meaning to say something to?
      </h2>

      <p className="text-sm text-muted-foreground text-center mb-8">
        This only helps guide tone. You can change everything later.
      </p>

      <div className="space-y-3 mb-8">
        {intentOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => onSelectIntent(option.value)}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              selectedIntent === option.value
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-border hover:border-primary/50 text-foreground/80'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <Button 
        onClick={onNext} 
        size="lg" 
        className="w-full gap-2"
        disabled={!selectedIntent}
      >
        Continue
        <ArrowRight className="h-4 w-4" />
      </Button>
    </motion.div>
  );
}

// Screen 3: Emotional Permission
function Screen3Permission({
  onChooseStyle,
  onWriteOwn,
}: {
  onChooseStyle: () => void;
  onWriteOwn: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-8 md:p-10 text-center"
    >
      <div className="mb-6">
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="inline-flex p-4 rounded-full bg-amber-100 dark:bg-amber-900/30"
        >
          <Sparkles className="h-10 w-10 text-amber-500" />
        </motion.div>
      </div>

      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
        You don't need perfect words.
      </h2>

      <p className="text-muted-foreground text-lg leading-relaxed mb-8">
        What matters is that they're yours.
        <br />
        We can help you start — or you can write freely.
      </p>

      <div className="space-y-3">
        <Button onClick={onChooseStyle} size="lg" className="w-full gap-2">
          Choose a Style
          <ArrowRight className="h-4 w-4" />
        </Button>
        <Button 
          onClick={onWriteOwn} 
          variant="ghost" 
          size="lg" 
          className="w-full text-muted-foreground"
        >
          I'll write on my own
        </Button>
      </div>
    </motion.div>
  );
}

// Hook to check if user needs onboarding
export function useOnboardingStatus() {
  const { user } = useAuth();
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [abTestVariant, setAbTestVariant] = useState<'full' | 'condensed'>('full');

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Check if user has any announcements (if they do, skip onboarding)
      const { count } = await supabase
        .from('announcements')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const isFirstTime = count === 0;
      setNeedsOnboarding(isFirstTime);

      // A/B test assignment (persisted via localStorage)
      if (isFirstTime) {
        const storedVariant = localStorage.getItem('onboarding_ab_variant');
        if (storedVariant === 'full' || storedVariant === 'condensed') {
          setAbTestVariant(storedVariant);
        } else {
          // Randomly assign variant
          const variant = Math.random() < 0.5 ? 'full' : 'condensed';
          localStorage.setItem('onboarding_ab_variant', variant);
          setAbTestVariant(variant);
        }
      }

      setIsLoading(false);
    };

    checkOnboardingStatus();
  }, [user]);

  const completeOnboarding = () => {
    setNeedsOnboarding(false);
  };

  return {
    needsOnboarding,
    isLoading,
    abTestVariant,
    completeOnboarding,
  };
}
